import { useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { crInstant, crDateOf } from "@despejo/domain";
import { DeadlineText, FilingChip, Icon, MoneyText, PaymentChip, StatusChip, formatDateEs } from "@despejo/ui";
import { periodText } from "../lib/dashboard.js";
import { must, supabase } from "../lib/supabase.js";
import { uploadDocument } from "../lib/upload.js";
import type { ObligationRow } from "../lib/types.js";
import { FilingHistory, FilingPackage } from "./FilingPackage.js";
import { IvaCalculation } from "./IvaCalculation.js";

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between", py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ textAlign: "right" }}>{children}</Box>
    </Stack>
  );
}

function FilePick({ file, onChange, label }: { file: File | null; onChange: (f: File | null) => void; label: string }) {
  return (
    <Button component="label" variant="outlined" startIcon={<Icon name="upload_file" />} sx={{ justifyContent: "flex-start" }}>
      {file ? file.name : label}
      <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg,.xml" onChange={(e) => onChange(e.target.files?.[0] ?? null)} />
    </Button>
  );
}

const today = () => crDateOf(new Date());

export function ObligationDialog({ row, onClose, onChanged }: { row: ObligationRow | null; onClose: () => void; onChanged: () => void }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [date, setDate] = useState(today());
  const [time, setTime] = useState("10:00");
  const [amount, setAmount] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("");
  const [expected, setExpected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!row) return null;
  const overdue = row.is_overdue_filing || row.is_overdue_payment;
  const filed = !["NOT_FILED", "NOT_APPLICABLE", "WAIVED"].includes(row.filing_status);
  const amountOk = amount === "" || /^\d+(\.\d{1,5})?$/.test(amount.replace(/\s/g, "").replace(",", "."));
  const normAmount = amount === "" ? null : amount.replace(/\s/g, "").replace(",", ".");

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (kind: "FILING_RECEIPT" | "PAYMENT_RECEIPT") =>
    file
      ? (
          await uploadDocument({
            organizationId: row.organization_id,
            taxpayerId: row.taxpayer_id,
            file,
            kind,
            periodKey: row.period_key,
            obligationId: row.id,
            title: `${kind === "FILING_RECEIPT" ? "Comprobante de presentación" : "Comprobante de pago"} ${row.form ?? row.obligation_code} ${row.period_key}`,
          })
        ).id
      : null;

  const recordFiling = () =>
    run(async () => {
      const receipt = await upload("FILING_RECEIPT");
      must(
        await supabase.rpc("record_filing", {
          p_obligation_id: row.id,
          p_filed_at: crInstant(date, time).toISOString(),
          p_amount_declared: normAmount,
          p_receipt_document_id: receipt,
          p_reason: filed ? reason || null : null,
        }),
      );
    });

  const recordPayment = () =>
    run(async () => {
      if (!normAmount) throw new Error("Indique el monto pagado");
      const receipt = await upload("PAYMENT_RECEIPT");
      must(await supabase.rpc("record_payment", { p_obligation_id: row.id, p_paid_at: crInstant(date, time).toISOString(), p_amount: normAmount, p_receipt_document_id: receipt }));
    });

  const markNotApplicable = () =>
    run(async () => {
      must(await supabase.rpc("set_obligation_not_applicable", { p_obligation_id: row.id, p_status: "NOT_APPLICABLE", p_reason: reason, p_source: source }));
    });

  const saveExpected = () =>
    run(async () => {
      const v = expected.replace(/\s/g, "").replace(",", ".");
      must(await supabase.from("taxpayer_obligations").update({ expected_amount: v || null, expected_amount_source: v ? "MANUAL" : null }).eq("id", row.id));
    });

  return (
    <Dialog open onClose={onClose} fullScreen={fullScreen} fullWidth maxWidth="md" aria-labelledby="ob-title">
      <DialogTitle id="ob-title">
        <Typography variant="caption" color="text.secondary" component="div">
          {row.taxpayer_name}
        </Typography>
        {row.name_es}
        {row.form ? ` (${row.form})` : ""} — {periodText(row.period_key)}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: "wrap", mb: 1 }}>
            {overdue && <StatusChip tone="error" icon="error" label="Vencida" />}
            {row.provisional && <StatusChip tone="warning" icon="help" label="Fecha por confirmar" />}
            {row.has_filing !== false && <FilingChip status={row.filing_status} />}
            {row.has_payment && <PaymentChip status={row.payment_status} />}
          </Stack>
          <Field label="Vence">
            <DeadlineText date={row.due_date} overdue={overdue} />
          </Field>
          {row.legal_due_date !== row.due_date && <Field label="Fecha legal original">{formatDateEs(row.legal_due_date)}</Field>}
          <Field label="Periodo">{periodText(row.period_key)}</Field>
          {row.has_payment && (
            <>
              <Field label="Monto esperado">
                <MoneyText amount={row.expected_amount} variant="body2" />
              </Field>
              <Field label="Monto declarado">
                <MoneyText amount={row.amount_due} variant="body2" />
              </Field>
              <Field label="Pagado">
                <MoneyText amount={row.amount_paid} variant="body2" />
              </Field>
            </>
          )}
          {row.status_reason && <Field label="Motivo">{row.status_reason}</Field>}
          {row.provisional && (
            <Alert severity="warning" icon={<Icon name="help" />} sx={{ mt: 1 }}>
              Esta fecha viene de una regla aún no confirmada contra la fuente oficial. Verifíquela en TRIBU-CR.
            </Alert>
          )}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" aria-label="Acciones">
          <Tab label={filed ? "Rectificar" : "Presentación"} />
          {row.has_payment && <Tab label="Pago" />}
          <Tab label="Monto esperado" />
          {!filed && <Tab label="No aplica" />}
          {row.obligation_code === "IVA_GENERAL" && <Tab label="Cálculo" value={9} />}
          {row.obligation_code === "IVA_GENERAL" && <Tab label="Paquete de declaración" value={10} />}
          <Tab label="Historial" value={11} />
        </Tabs>
        {tab === 9 && (
          <Box sx={{ mt: 2 }}>
            <IvaCalculation row={row} onSaved={onChanged} />
          </Box>
        )}
        {tab === 10 && (
          <Box sx={{ mt: 2 }}>
            <FilingPackage row={row} onChanged={onChanged} />
          </Box>
        )}
        {tab === 11 && (
          <Box sx={{ mt: 2 }}>
            <FilingHistory obligationId={row.id} />
          </Box>
        )}
        <Stack spacing={2} sx={{ mt: 2 }}>
          {(tab === 0 || (row.has_payment && tab === 1)) && (
            <Stack direction="row" spacing={2}>
              <TextField label="Fecha" type="date" value={date} onChange={(e) => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField label="Hora (Costa Rica)" type="time" value={time} onChange={(e) => setTime(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
          )}
          {tab === 0 && (
            <>
              {row.obligation_code === "IVA_GENERAL" && !filed && (
                <Alert severity="info">
                  Para una declaración preparada en Despejo use la pestaña “Paquete de declaración”: valida el periodo y guarda la huella del paquete. Aquí registre solo
                  presentaciones hechas fuera de Despejo.
                </Alert>
              )}
              <TextField label={row.has_payment ? "Monto declarado (₡)" : "Monto (opcional)"} value={amount} onChange={(e) => setAmount(e.target.value)} error={!amountOk} inputMode="decimal" />
              {filed && <TextField label="Motivo de la rectificación" required value={reason} onChange={(e) => setReason(e.target.value)} />}
              <FilePick file={file} onChange={setFile} label="Adjuntar comprobante de presentación" />
            </>
          )}
          {row.has_payment && tab === 1 && (
            <>
              <TextField label="Monto pagado (₡)" required value={amount} onChange={(e) => setAmount(e.target.value)} error={!amountOk} inputMode="decimal" />
              <FilePick file={file} onChange={setFile} label="Adjuntar comprobante de pago" />
            </>
          )}
          {tab === (row.has_payment ? 2 : 1) && (
            <TextField label="Monto esperado (₡)" helperText="Del periodo anterior, una estimación o el borrador de TRIBU-CR" value={expected} onChange={(e) => setExpected(e.target.value)} inputMode="decimal" />
          )}
          {!filed && tab === (row.has_payment ? 3 : 2) && (
            <>
              <Alert severity="info">Marcar como “no aplica” requiere un motivo y la fuente que lo respalda (por ejemplo, la Consulta Integral Hacendaria).</Alert>
              <TextField label="Motivo" required value={reason} onChange={(e) => setReason(e.target.value)} />
              <TextField label="Fuente" required value={source} onChange={(e) => setSource(e.target.value)} />
            </>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {tab === 0 && (
          <Button variant="contained" disabled={busy || !amountOk || (filed && !reason)} onClick={recordFiling}>
            {filed ? "Registrar rectificación" : row.obligation_code === "IVA_GENERAL" ? "Registrar presentación externa" : "Registrar presentación"}
          </Button>
        )}
        {row.has_payment && tab === 1 && (
          <Button variant="contained" disabled={busy || !amountOk || !amount} onClick={recordPayment}>
            Registrar pago
          </Button>
        )}
        {tab === (row.has_payment ? 2 : 1) && (
          <Button variant="contained" disabled={busy} onClick={saveExpected}>
            Guardar
          </Button>
        )}
        {!filed && tab === (row.has_payment ? 3 : 2) && (
          <Button variant="contained" color="error" disabled={busy || !reason || !source} onClick={markNotApplicable}>
            Marcar como no aplica
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
